import { AppRoutes } from "@/routes/config";
import { logout } from "@/utils/logout";
import { Key } from "react";

const CENTER_URL = (process.env.REACT_APP_ADMIN_CENTER_URL ?? "").replace(/\/$/, "");

/**
 * ex: /company/:id => /company/1
 * @param {string} pathname - pathname is define in AppRoutes
 * @param {number} params - Object to replace params in dynamic route
 * @returns {string} - DynamicRoute.
 *
 */
export const getDynamicRoute = (pathname: string, params: Record<string, Key | undefined>, searchParams?: string): string => {
  const route = Object.entries(params).reduce((cur, [key, value]) => (value ? cur.replace(`:${key}`, value.toString()) : cur), pathname);
  return searchParams ? `${route}?${searchParams}` : route;
};

/**
 * ex: /company/:id => /company/1
 * @param {string} url - origin url
 * @param {number} params - Object to create search param
 * @returns {string} - url with search param.
 *
 */
export const createUrlWithParams = <D = object>(url: string, data: D): string => {
  if (!data) {
    return url;
  }

  const dataAry = Object.entries<number | string | boolean | string[] | number[]>(data).filter(([_, value]) => {
    return !(value === null || value === undefined);
  });
  if (!dataAry.length) {
    return url;
  }

  const params = dataAry.map(([key, value]) => {
    // For arrays use the same key and create a key=value string for each value
    if (Array.isArray(value)) {
      return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join("&");
    }

    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  });

  return [url, params.join("&")].join("?");
};

/**
 * @param {string} url - url to verify
 * @returns {boolean} - true isUrl
 */
export const isUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Builds the set of origins that user-supplied redirects may target.
 * @returns {Set<string>} - allow-listed origins (current origin + REACT_APP_ALLOWED_REDIRECT_ORIGINS)
 */
const getAllowedRedirectOrigins = (): Set<string> => {
  const origins = new Set<string>([window.location.origin]);
  const allowList = (process.env.REACT_APP_ALLOWED_REDIRECT_ORIGINS ?? "").split(",");
  allowList.forEach((value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    try {
      origins.add(new URL(trimmed).origin);
    } catch {
      // Ignore malformed env values.
    }
  });
  return origins;
};

/**
 * Validates a user-supplied redirect target to prevent open-redirect attacks.
 * @param {string | null | undefined} url - url to verify
 * @returns {boolean} - true if the url is a safe (internal or allow-listed) redirect target
 */
export const isValidRedirectUrl = (url: string | null | undefined): url is string => {
  if (!url || typeof url !== "string") return false;
  if (url.startsWith("/")) {
    // Reject protocol-relative ("//evil.com") and backslash ("/\\evil.com") tricks.
    return !url.startsWith("//") && !url.startsWith("/\\");
  }

  try {
    const parsed = new URL(url);
    const isHttp = parsed.protocol === "http:" || parsed.protocol === "https:";
    return isHttp && getAllowedRedirectOrigins().has(parsed.origin);
  } catch {
    return false;
  }
};

/**
 * Resolves a safe redirect target, falling back to home when the url is invalid.
 * @param {string | null | undefined} url - user-supplied redirect target
 * @param {string} fallback - if url invalid, use fallback instead
 * @returns {string} - the url when valid, otherwise the fallback
 */
export const getSafeRedirectURL = (url: string | null | undefined, fallback = AppRoutes.home): string => {
  return isValidRedirectUrl(url) ? url : fallback;
};

/**
 * @returns {string} - return tenant domain
 */

export const getTenantDomain = (): string => {
  const url = window.location.hostname;
  return url.replace("admin.", "");
};

/**
 * @param {string} url - url to verify
 * @returns {boolean} - true isInternalTenant
 */
export const isInternalTenantLink = (url: string): boolean => {
  try {
    const linkUrl = new URL(url);
    return linkUrl.hostname.includes(getTenantDomain());
  } catch (error) {
    return false;
  }
};

/**
 * Extracts query parameters from a given URL and returns them as an object.
 *
 * @template T - Type of the query parameters. Should be a record where keys are strings and values are strings or arrays of strings or undefined.
 * @param {string} url - The URL from which to extract query parameters.
 * @returns {T | null} - An object containing the query parameters, where keys are parameter names and values are their corresponding values. Returns `null` if an error occurs.
 */
export const getQuery = <T extends Record<string, string | string[] | undefined>>(url: string): T | null => {
  try {
    const { searchParams } = new URL(url); // Create a URL object and extract searchParams
    const initData: Record<string, string | string[] | undefined> = {}; // Initialize an empty object to store query parameters

    return Array.from(searchParams).reduce((cur, [key, value]) => {
      const curValue = cur[key];
      if (curValue) {
        // If the key already exists, merge the new value with the existing value(s)
        cur[key] = [curValue, value].flat();
        return cur;
      }
      cur[key] = value; // Otherwise, set the key with the new value
      return cur;
    }, initData) as T; // Cast the result to the specified type T
  } catch {
    return null; // Return null if an error occurs during URL parsing or query extraction
  }
};

/**
 * Extracts query parameters from the current URL.
 *
 * @template T - Type of the query parameters. Should be a record where keys are strings and values are strings or arrays of strings or undefined.
 * @returns {T | null} - An object containing the query parameters, or `null` if no parameters are found.
 */
export const getQueryFromURL = <T extends Record<string, string | string[] | undefined>>(): T | null => {
  return getQuery(window.location.toString());
};

/**
 * Extracts dynamic route parameters from a given origin and pathname.
 *
 * @param {string} origin - The origin part of the URL, typically containing dynamic segments (e.g., "/users/:userId/posts/:postId").
 * @param {string} pathname - The actual path of the URL (e.g., "/users/123/posts/456").
 * @returns {Record<string, string>} - An object mapping dynamic segment names to their values (e.g., { userId: "123", postId: "456" }).
 */
export const getParams = (origin: string, pathname: string): Record<string, string> => {
  const originSplit = origin.split("/"); // Split the origin into segments based on "/"
  const pathnameSplit = pathname.split("/"); // Split the pathname into segments based on "/"
  const params: Record<string, string> = {}; // Object to store the extracted parameters

  originSplit.forEach((item, index) => {
    if (item.startsWith(":")) {
      const key = item.replace(":", ""); // Remove ":" to get the parameter key
      const value = pathnameSplit[index]; // Get the corresponding value from the pathname
      if (key && value) {
        params[key] = value; // Assign the value to the parameter key
      }
    }
  });

  return params; // Return the object containing the extracted parameters
};

/**
 * Clears the invalid session and hands the browser to /login, carrying the screen
 * the admin was on (so login can bounce them straight back) and their email (so
 * the login form stays pre-filled).
 *
 * An existing `?redirectUrl` wins over the current location: when this fires on
 * /login itself, re-capturing `location` would nest the login route inside its
 * own redirectUrl. Both candidates go through `getSafeRedirectURL` so a crafted
 * `?redirectUrl` can't turn the login bounce into an open redirect.
 */
export const redirectToLogin = async () => {
  const email = await logout();
  const existingRedirect = new URLSearchParams(location.search).get("redirectUrl");
  // `location.pathname` is sanitized too: on a "https://host//evil.com" url it reads
  // back as the protocol-relative "//evil.com".
  const currentUrl = getSafeRedirectURL(`${location.pathname}${location.search}`);
  const oldUrl = getSafeRedirectURL(existingRedirect, currentUrl);
  location.href = createUrlWithParams(AppRoutes.login, { redirectUrl: oldUrl, email });
};

/**
 * Leaves this app for the irohana center, used when the center tab is no longer reachable.
 *
 * @param {string} pathname - center-side path to land on (leading slash optional)
 * @returns {boolean} - false when REACT_APP_ADMIN_CENTER_URL is missing/invalid (nothing happened),
 * so the caller can fall back to an in-app route
 */
export const redirectToCenter = (pathname: string) => {
  if (!isUrl(CENTER_URL)) return false;
  return window.location.replace(`${CENTER_URL}${pathname}`);
};

/**
 * Query param that carries the center pathname a handoff started from, so the initial-registration
 * flow can send the admin back to that exact screen instead of guessing a center route.
 * Set by /auth/callback from the `sourcePath` the center puts on the handoff URL.
 */
export const CENTER_FROM_PARAM = "centerFrom";

/**
 * Guards a center pathname before it is trusted as a redirect target: internal paths only, so a
 * crafted `centerFrom` can never point at another origin (`//evil.com`, `/\evil.com`, `https://...`).
 * @param {string | null | undefined} path - candidate center pathname
 * @returns {boolean} - true when the value is a safe internal path
 */
export const isInternalPath = (path: string | null | undefined): path is string => {
  if (!path || typeof path !== "string") return false;
  return path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\");
};

/**
 * Reads the validated `centerFrom` pathname out of a query string (defaults to the current URL).
 * @param {string} search - query string to read, e.g. `location.search`
 * @returns {string | undefined} - the center pathname, or undefined when absent/unsafe
 */
export const getCenterFrom = (search: string = window.location.search): string | undefined => {
  const value = new URLSearchParams(search).get(CENTER_FROM_PARAM);
  return isInternalPath(value) ? value : undefined;
};

/**
 * Appends `centerFrom` to an internal route when there is a center pathname to carry.
 * @param {string} route - internal route of this app
 * @param {string | null | undefined} centerFrom - center pathname to carry through the flow
 * @returns {string} - the route, with the param appended only when the pathname is safe
 */
export const withCenterFrom = (route: string, centerFrom: string | null | undefined): string => {
  if (!isInternalPath(centerFrom)) return route;
  return createUrlWithParams(route, { [CENTER_FROM_PARAM]: centerFrom });
};
